import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  List, X, Heart, Shield, Users, Brain, ChartLine, 
  ClipboardText, GraduationCap, Warning, Phone, EnvelopeSimple,
  MapPin, LinkedinLogo, TwitterLogo, ArrowRight, Check,
  Calendar, Clock, CaretDown, Star, Desktop
} from "@phosphor-icons/react";

import HomePage from "./pages/HomePage.jsx";
import ServicesPage from "./pages/ServicesPage.jsx";
import AboutPage from "./pages/AboutPage.jsx";
import ContactPage from "./pages/ContactPage.jsx";
import PlatformDemo from "./pages/PlatformDemo.jsx";
import SuccessStoriesPage from "./pages/SuccessStoriesPage.jsx";
import ResourcesPage from "./pages/ResourcesPage.jsx";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage.jsx";
import ProductsPage from "./pages/ProductsPage.jsx";
import ScheduleDemoPage from "./pages/ScheduleDemoPage.jsx";
import AdminDashboard from "./pages/AdminDashboard.jsx";
import AIOnboardingPage from "./pages/AIOnboardingPage.jsx";
import AITrainingPage from "./pages/AITrainingPage.jsx";
import HealthScorePage from "./pages/HealthScorePage.jsx";
import ROICalculatorPage from "./pages/ROICalculatorPage.jsx";
import ClientPortalPage from "./pages/ClientPortalPage.jsx";
import RegulatoryUpdatesPage from "./pages/RegulatoryUpdatesPage.jsx";
import WebinarsPage from "./pages/WebinarsPage.jsx";
import BenchmarkReportsPage from "./pages/BenchmarkReportsPage.jsx";
import CertificationBadgesPage from "./pages/CertificationBadgesPage.jsx";
import ClientOnboardingPage from "./pages/ClientOnboardingPage.jsx";
import TestimonialCollectionPage from "./pages/TestimonialCollectionPage.jsx";
import BlogPage from "./pages/BlogPage.jsx";
import BlogArticlePage from "./pages/BlogArticlePage.jsx";
import HomeHealthServicesPage from "./pages/HomeHealthServicesPage.jsx";
import ChatWidget from "./components/ChatWidget/index.jsx";

// SEO Landing Pages
import HospiceAuditsPage from "./pages/SEO/HospiceAuditsPage.jsx";
import HospiceSurveysPage from "./pages/SEO/HospiceSurveysPage.jsx";
import HospiceAIPage from "./pages/SEO/HospiceAIPage.jsx";
import HospiceConsultingPage from "./pages/SEO/HospiceConsultingPage.jsx";
import PDGMPage from "./pages/SEO/PDGMPage.jsx";

// Theme Context
const ThemeContext = createContext();

export const useTheme = () => useContext(ThemeContext);

// Navigation Component - Futuristic Glassmorphism
const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { theme, setTheme } = useTheme();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { path: "/", label: "Home" },
    { path: "/services", label: "Services" },
    { path: "/products", label: "Products" },
    { path: "/blog", label: "Blog" },
    { path: "/resources", label: "Resources" },
    { path: "/about", label: "About" },
    { path: "/contact", label: "Contact" },
  ];

  const toolLinks = [
    { path: "/tools/health-score", label: "Hospice Health Score" },
    { path: "/tools/roi-calculator", label: "ROI Calculator" },
    { path: "/tools/regulatory-updates", label: "Regulatory Updates" },
    { path: "/tools/webinars", label: "Webinars & Training" },
    { path: "/tools/benchmarks", label: "Benchmark Reports" },
    { path: "/tools/certifications", label: "Certification Badges" },
    { path: "/client-portal", label: "Client Portal" },
  ];

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      scrolled ? 'glass border-b border-slate-200/50 shadow-lg' : 'bg-transparent'
    }`}>
      <nav className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group" data-testid="nav-logo">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 flex items-center justify-center shadow-lg shadow-cyan-500/20 group-hover:shadow-cyan-500/40 transition-shadow">
              <Heart weight="fill" className="w-5 h-5 text-white" />
            </div>
            <div>
              <span className="font-display font-bold text-lg text-slate-900">Noble</span>
              <span className="text-xs text-slate-500 block -mt-1">Hospice Solutions</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`nav-link text-sm font-medium ${
                  location.pathname === link.path ? "active text-slate-900" : "text-slate-600"
                }`}
                data-testid={`nav-${link.label.toLowerCase()}`}
              >
                {link.label}
              </Link>
            ))}
            {/* Tools Dropdown */}
            <div className="relative group">
              <button className="nav-link text-sm font-medium flex items-center gap-1 text-slate-600">
                Tools
                <CaretDown weight="bold" className="w-3 h-3" />
              </button>
              <div className="absolute top-full left-0 pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all">
                <div className="glass-card rounded-xl shadow-xl py-2 min-w-[220px]">
                  {toolLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      className="block px-4 py-2.5 text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-100/50 transition-colors"
                    >
                      {link.label}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Theme Toggle & CTA */}
          <div className="hidden md:flex items-center gap-4">
            <div className="theme-toggle">
              <button
                className={`theme-toggle-button ${theme === "compassion" ? "active" : ""}`}
                onClick={() => setTheme("compassion")}
                data-testid="theme-compassion"
              >
                Light
              </button>
              <button
                className={`theme-toggle-button ${theme === "clinical" ? "active" : ""}`}
                onClick={() => setTheme("clinical")}
                data-testid="theme-clinical"
              >
                Dark
              </button>
            </div>
            <Link
              to="/contact"
              className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white h-10 px-6 rounded-full font-medium transition-all hover:shadow-lg hover:shadow-cyan-500/30 hover:-translate-y-0.5 flex items-center gap-2 text-sm"
              data-testid="nav-cta"
            >
              Get Started
              <ArrowRight weight="bold" className="w-4 h-4" />
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-slate-900"
            onClick={() => setIsOpen(!isOpen)}
            data-testid="mobile-menu-toggle"
          >
            {isOpen ? (
              <X weight="bold" className="w-6 h-6" />
            ) : (
              <List weight="bold" className="w-6 h-6" />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden border-t border-slate-200"
            >
              <div className="py-4 space-y-2">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`block py-3 px-4 rounded-xl text-sm font-medium ${
                      location.pathname === link.path
                        ? "bg-gradient-to-r from-cyan-500/10 to-blue-500/10 text-cyan-600"
                        : "text-slate-600 hover:bg-slate-100"
                    }`}
                    onClick={() => setIsOpen(false)}
                    data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                  >
                    {link.label}
                  </Link>
                ))}
                <div className="pt-4 px-4">
                  <div className="theme-toggle mb-4">
                    <button
                      className={`theme-toggle-button ${theme === "compassion" ? "active" : ""}`}
                      onClick={() => setTheme("compassion")}
                    >
                      Light
                    </button>
                    <button
                      className={`theme-toggle-button ${theme === "clinical" ? "active" : ""}`}
                      onClick={() => setTheme("clinical")}
                    >
                      Dark
                    </button>
                  </div>
                  <Link
                    to="/contact"
                    className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 text-white h-12 rounded-full font-medium flex items-center justify-center gap-2"
                    onClick={() => setIsOpen(false)}
                  >
                    Get Started
                    <ArrowRight weight="bold" className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </header>
  );
};

// Footer Component - Futuristic Dark
const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-slate-900 text-white py-20 md:py-28 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-0 left-1/4 w-96 h-96 rounded-full" style={{ background: 'radial-gradient(circle, rgba(6, 182, 212, 0.15) 0%, transparent 70%)' }} />
        <div className="absolute bottom-0 right-1/4 w-80 h-80 rounded-full" style={{ background: 'radial-gradient(circle, rgba(99, 102, 241, 0.1) 0%, transparent 70%)' }} />
      </div>
      
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-16">
          {/* Brand */}
          <div className="md:col-span-1">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 flex items-center justify-center">
                <Heart weight="fill" className="w-5 h-5 text-white" />
              </div>
              <div>
                <span className="font-display font-bold text-lg text-white">Noble</span>
                <span className="text-xs text-slate-400 block -mt-1">Hospice Solutions</span>
              </div>
            </div>
            <p className="text-slate-400 text-sm leading-relaxed">
              Where hospice leadership meets intelligent automation. Built by hospice leaders, for hospice leaders.
            </p>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-display font-semibold text-white mb-6">Services</h4>
            <ul className="space-y-3 text-sm text-slate-400">
              <li><Link to="/services#operations" className="hover:text-cyan-400 transition-colors">Operations Optimization</Link></li>
              <li><Link to="/services#compliance" className="hover:text-cyan-400 transition-colors">Compliance & Regulatory</Link></li>
              <li><Link to="/services#ai-intelligence" className="hover:text-cyan-400 transition-colors">AI Workflow Design</Link></li>
              <li><Link to="/solutions/onboarding" className="hover:text-cyan-400 transition-colors">AI Onboarding Systems</Link></li>
              <li><Link to="/solutions/training" className="hover:text-cyan-400 transition-colors">AI Training Platform</Link></li>
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="font-display font-semibold text-white mb-6">Company</h4>
            <ul className="space-y-3 text-sm text-slate-400">
              <li><Link to="/about" className="hover:text-cyan-400 transition-colors">About Us</Link></li>
              <li><Link to="/products" className="hover:text-cyan-400 transition-colors">Our Products</Link></li>
              <li><Link to="/resources" className="hover:text-cyan-400 transition-colors">Resources & Blog</Link></li>
              <li><Link to="/contact" className="hover:text-cyan-400 transition-colors">Contact</Link></li>
              <li><Link to="/privacy-policy" className="hover:text-cyan-400 transition-colors">Privacy Policy</Link></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-display font-semibold text-white mb-6">Contact</h4>
            <ul className="space-y-4 text-sm text-slate-400">
              <li className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-slate-800 flex items-center justify-center">
                  <EnvelopeSimple className="w-4 h-4 text-cyan-400" />
                </div>
                <a href="mailto:noblehospicesolutions@gmail.com" className="hover:text-cyan-400 transition-colors">
                  noblehospicesolutions@gmail.com
                </a>
              </li>
              <li className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-slate-800 flex items-center justify-center">
                  <Phone className="w-4 h-4 text-cyan-400" />
                </div>
                <a href="tel:+17183046214" className="hover:text-cyan-400 transition-colors">
                  +1 (718) 304-6214
                </a>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-slate-800 flex items-center justify-center flex-shrink-0">
                  <Clock className="w-4 h-4 text-cyan-400" />
                </div>
                <span>Mon - Fri: 8AM - 6PM PST<br />Sat - Sun: 8AM - 6PM EST</span>
              </li>
            </ul>
            <div className="flex gap-3 mt-6">
              <a href="#" className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center text-slate-400 hover:text-cyan-400 hover:bg-slate-700 transition-all" data-testid="footer-linkedin">
                <LinkedinLogo weight="fill" className="w-5 h-5" />
              </a>
              <a href="#" className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center text-slate-400 hover:text-cyan-400 hover:bg-slate-700 transition-all" data-testid="footer-twitter">
                <TwitterLogo weight="fill" className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>

        <div className="border-t border-slate-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-slate-500">
            © {currentYear} Noble Hospice Solutions. All rights reserved.
          </p>
          <p className="text-sm text-slate-500">
            Intelligent Compliance for Compassionate Care
          </p>
        </div>
      </div>
    </footer>
  );
};

function App() {
  const [theme, setTheme] = useState("compassion");

  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
      <div className={`App ${theme === "clinical" ? "theme-clinical" : ""}`}>
        <BrowserRouter>
          <Navigation />
          <main className="pt-20">
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/services" element={<ServicesPage />} />
              <Route path="/products" element={<ProductsPage />} />
              <Route path="/resources" element={<ResourcesPage />} />
              <Route path="/services/home-health" element={<HomeHealthServicesPage />} />
              <Route path="/blog" element={<BlogPage />} />
              <Route path="/blog/:slug" element={<BlogArticlePage />} />
              <Route path="/success-stories" element={<SuccessStoriesPage />} />
              <Route path="/platform" element={<PlatformDemo />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
              <Route path="/schedule-demo" element={<ScheduleDemoPage />} />
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/solutions/onboarding" element={<AIOnboardingPage />} />
              <Route path="/solutions/training" element={<AITrainingPage />} />
              <Route path="/tools/health-score" element={<HealthScorePage />} />
              <Route path="/tools/roi-calculator" element={<ROICalculatorPage />} />
              <Route path="/tools/regulatory-updates" element={<RegulatoryUpdatesPage />} />
              <Route path="/tools/webinars" element={<WebinarsPage />} />
              <Route path="/tools/benchmarks" element={<BenchmarkReportsPage />} />
              <Route path="/tools/certifications" element={<CertificationBadgesPage />} />
              <Route path="/client-portal" element={<ClientPortalPage />} />
              <Route path="/client-onboarding" element={<ClientOnboardingPage />} />
              <Route path="/testimonial" element={<TestimonialCollectionPage />} />
              
              {/* SEO Landing Pages */}
              <Route path="/hospice-audits" element={<HospiceAuditsPage />} />
              <Route path="/hospice-surveys" element={<HospiceSurveysPage />} />
              <Route path="/hospice-ai" element={<HospiceAIPage />} />
              <Route path="/hospice-consulting" element={<HospiceConsultingPage />} />
              <Route path="/pdgm" element={<PDGMPage />} />
            </Routes>
          </main>
          <Footer />
          <ChatWidget />
        </BrowserRouter>
      </div>
    </ThemeContext.Provider>
  );
}

export default App;
